﻿import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import {
  Plus,
  Wallet,
  Pencil,
  Pause,
  Play,
  X,
  IndianRupee,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  CalendarDays,
  History,
  Download,
} from 'lucide-react';
import {
  PERMISSIONS,
  type ApiError,
  type EmployeeLoan,
  type EmployeeAdvance,
  type SalaryOverviewRecord,
} from '@pms/shared';
import { payrollService } from '@/services/payroll.service';
import { usePermission } from '@/hooks/usePermission';
import { formatHoursMinutes } from '@/lib/duration';
import { LOAN_STATUS_COLORS, ADVANCE_STATUS_COLORS } from '@/components/payroll/loan-status';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs } from '@/components/ui/tabs';
import { DataTable, type Column } from '@/components/ui/data-table';
import { AddLoanModal } from '@/components/payroll/AddLoanModal';
import { EditLoanModal } from '@/components/payroll/EditLoanModal';
import { AddAdvanceModal } from '@/components/payroll/AddAdvanceModal';
import { EditAdvanceModal } from '@/components/payroll/EditAdvanceModal';
import { LoanScheduleModal } from '@/components/payroll/LoanScheduleModal';
import { RecordLoanPaymentModal } from '@/components/payroll/RecordLoanPaymentModal';
import { LoanHistoryModal } from '@/components/payroll/LoanHistoryModal';
import { SalaryGate } from '@/components/payroll/SalaryGate';

function getErrorMessage(e: unknown): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? 'Something went wrong';
}

function formatCurrency(val: string | number): string {
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

/** Compact right-aligned styling for the numeric salary-overview columns. */
const NUM_COL = 'px-2 text-right whitespace-nowrap';

function HoursCell({ minutes }: { minutes: number }) {
  if (!minutes || minutes <= 0) return <span className="text-muted-foreground">—</span>;
  return <span>{formatHoursMinutes(minutes)}</span>;
}

function formatMonth(year: number | null, month: number | null): string {
  if (!year || !month) return '—';
  return new Date(year, month - 1).toLocaleString('en-IN', { month: 'short', year: 'numeric' });
}

const TAB_KEYS = ['salary', 'loans'] as const;
type TabKey = (typeof TAB_KEYS)[number];

const TAB_LABELS: Record<TabKey, string> = {
  loans:  'Loans & Advances',
  salary: 'Salary Overview',
};

export function HrmsPayrollPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = (searchParams.get('tab') as TabKey) || 'salary';
  const [active, setActive] = useState<TabKey>(TAB_KEYS.includes(initial) ? initial : 'salary');

  const onChange = (key: string) => {
    setActive(key as TabKey);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (key === 'salary') next.delete('tab');
      else next.set('tab', key);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Payroll</h1>
        <p className="text-sm text-muted-foreground">
          Manage employee loans, salary advances, and payroll configuration.
        </p>
      </div>

      <Tabs
        tabs={TAB_KEYS.map((k) => ({ key: k, label: TAB_LABELS[k] }))}
        active={active}
        onChange={onChange}
      />

      {/* The tab strip stays visible so the page is navigable, but every figure
          behind it is salary data and sits behind the pass key. */}
      <SalaryGate label="Payroll">
        {active === 'loans'  && <LoansTab />}
        {active === 'salary' && <SalaryOverviewTab />}
      </SalaryGate>
    </div>
  );
}

// ── Loans & Advances Tab ────────────────────────────────────────────────────────

function LoansTab() {
  const queryClient = useQueryClient();

  const { data: laConfig } = useQuery({
    queryKey: ['payroll', 'la-config'],
    queryFn: () => payrollService.getLAConfig(),
  });

  const [loanPage, setLoanPage] = useState(1);
  const [loanStatus, setLoanStatus] = useState('');
  const [advPage, setAdvPage] = useState(1);
  const [advStatus, setAdvStatus] = useState('');

  const [addLoanOpen, setAddLoanOpen] = useState(false);
  const [addAdvanceOpen, setAddAdvanceOpen] = useState(false);
  const [editLoan, setEditLoan] = useState<EmployeeLoan | null>(null);
  const [editAdvance, setEditAdvance] = useState<EmployeeAdvance | null>(null);
  const [closeLoanId, setCloseLoanId] = useState<string | null>(null);
  const [cancelAdvanceId, setCancelAdvanceId] = useState<string | null>(null);
  const [scheduleLoan, setScheduleLoan] = useState<EmployeeLoan | null>(null);
  const [payLoan, setPayLoan] = useState<EmployeeLoan | null>(null);
  const [historyLoan, setHistoryLoan] = useState<EmployeeLoan | null>(null);

  const { data: loansData, isLoading: loansLoading } = useQuery({
    queryKey: ['payroll', 'loans', { page: loanPage, status: loanStatus || undefined }],
    queryFn: () =>
      payrollService.listLoans({
        page: loanPage,
        limit: 20,
        status: (loanStatus as 'active' | 'paused' | 'closed') || undefined,
      }),
  });

  const { data: advData, isLoading: advLoading } = useQuery({
    queryKey: ['payroll', 'advances', { page: advPage, status: advStatus || undefined }],
    queryFn: () =>
      payrollService.listAdvances({
        page: advPage,
        limit: 20,
        status: (advStatus as 'pending' | 'deducted' | 'cancelled') || undefined,
      }),
  });

  const updateLoanStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'paused' | 'closed' }) =>
      payrollService.updateLoanStatus(id, { status }),
    onSuccess: () => {
      toast.success('Loan status updated');
      void queryClient.invalidateQueries({ queryKey: ['payroll', 'loans'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const cancelAdvance = useMutation({
    mutationFn: (id: string) => payrollService.cancelAdvance(id),
    onSuccess: () => {
      toast.success('Advance cancelled');
      void queryClient.invalidateQueries({ queryKey: ['payroll', 'advances'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const loans = loansData?.data ?? [];
  const advances = advData?.data ?? [];

  return (
    <div className="space-y-4">
      {/* ── Loans Card ─────────────────────────────────────────────────────── */}
      <div className="rounded-lg border border-border/60 bg-card">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border/40 px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold">Employee Loans</h2>
            <p className="text-xs text-muted-foreground">
              {laConfig
                ? `Max ${formatCurrency(laConfig.max_loan_amount)} per loan · deducted from salary automatically per the monthly schedule`
                : 'Deducted from salary automatically per the monthly schedule'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Select
              value={loanStatus || 'all'}
              onValueChange={(val) => { setLoanStatus(val === 'all' ? '' : val); setLoanPage(1); }}
            >
              <SelectTrigger className="h-9 w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => setAddLoanOpen(true)}>
              <Plus className="h-4 w-4" /> Loan
            </Button>
          </div>
        </div>

        {/* Content */}
        {loansLoading ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : loans.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">No loans found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60 text-left text-xs font-medium text-muted-foreground">
                  <th className="px-4 py-3">Employee</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">EMI</th>
                  <th className="px-4 py-3">Months Deducted</th>
                  <th className="px-4 py-3">Remaining</th>
                  <th className="px-4 py-3">Interest</th>
                  <th className="px-4 py-3">Start</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loans.map((loan) => {
                  const emisPaid = loan.emis_paid ?? 0;
                  const remaining =
                    loan.remaining_amount ??
                    (parseFloat(loan.amount) - emisPaid * parseFloat(loan.emi_amount));
                  return (
                    <tr
                      key={loan.id}
                      className="border-b border-border/40 last:border-0 hover:bg-muted/30"
                    >
                      <td className="px-4 py-3 font-medium">
                        {loan.first_name} {loan.last_name}
                        {loan.employee_code && (
                          <span className="ml-1 text-xs text-muted-foreground">
                            ({loan.employee_code})
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">{formatCurrency(loan.amount)}</td>
                      <td className="px-4 py-3">{formatCurrency(loan.emi_amount)}</td>
                      <td className="px-4 py-3">{emisPaid}/{loan.num_emis}</td>
                      <td className="px-4 py-3">{formatCurrency(Math.max(0, Number(remaining)))}</td>
                      <td className="px-4 py-3">{parseFloat(loan.interest_rate).toFixed(1)}%</td>
                      <td className="px-4 py-3 text-xs">{loan.start_date}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${LOAN_STATUS_COLORS[loan.status] ?? ''}`}
                        >
                          {loan.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          {loan.status === 'active' && (
                            <button
                              title="Record Payment"
                              className="rounded p-1 text-teal-600 hover:bg-teal-50"
                              onClick={() => setPayLoan(loan)}
                            >
                              <IndianRupee className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            title="Payment History"
                            className="rounded p-1 text-blue-600 hover:bg-blue-50"
                            onClick={() => setHistoryLoan(loan)}
                          >
                            <History className="h-4 w-4" />
                          </button>
                          <button
                            title="Deduction Schedule"
                            className="rounded p-1 text-indigo-600 hover:bg-indigo-50"
                            onClick={() => setScheduleLoan(loan)}
                          >
                            <CalendarDays className="h-4 w-4" />
                          </button>
                          {loan.status !== 'closed' && (
                            <button
                              title="Edit"
                              className="rounded p-1 text-muted-foreground hover:bg-muted"
                              onClick={() => setEditLoan(loan)}
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          )}
                          {loan.status === 'active' && (
                            <button
                              title="Pause"
                              className="rounded p-1 text-amber-600 hover:bg-amber-50"
                              onClick={() =>
                                updateLoanStatus.mutate({ id: loan.id, status: 'paused' })
                              }
                            >
                              <Pause className="h-4 w-4" />
                            </button>
                          )}
                          {loan.status === 'paused' && (
                            <button
                              title="Activate"
                              className="rounded p-1 text-teal-600 hover:bg-teal-50"
                              onClick={() =>
                                updateLoanStatus.mutate({ id: loan.id, status: 'active' })
                              }
                            >
                              <Play className="h-4 w-4" />
                            </button>
                          )}
                          {loan.status !== 'closed' && (
                            <button
                              title="Close"
                              className="rounded p-1 text-destructive hover:bg-destructive/10"
                              onClick={() => setCloseLoanId(loan.id)}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {loansData && loansData.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border/40 px-4 py-3 text-sm">
            <span className="text-muted-foreground">
              Page {loansData.pagination.page} of {loansData.pagination.totalPages} ·{' '}
              {loansData.pagination.total} total
            </span>
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="outline"
                disabled={loanPage <= 1}
                onClick={() => setLoanPage((p) => p - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={loanPage >= loansData.pagination.totalPages}
                onClick={() => setLoanPage((p) => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* ── Advances Card ───────────────────────────────────────────────────── */}
      <div className="rounded-lg border border-border/60 bg-card">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border/40 px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold">Salary Advances</h2>
            <p className="text-xs text-muted-foreground">
              {laConfig
                ? `Max ${formatCurrency(laConfig.max_advance_amount)} per advance · Deducted from specified month's salary`
                : 'Deducted from specified month\'s salary'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Select
              value={advStatus || 'all'}
              onValueChange={(val) => { setAdvStatus(val === 'all' ? '' : val); setAdvPage(1); }}
            >
              <SelectTrigger className="h-9 w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="deducted">Deducted</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => setAddAdvanceOpen(true)}>
              <Wallet className="h-4 w-4" /> Advance
            </Button>
          </div>
        </div>

        {/* Content */}
        {advLoading ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : advances.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">No advances found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60 text-left text-xs font-medium text-muted-foreground">
                  <th className="px-4 py-3">Employee</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Advance Date</th>
                  <th className="px-4 py-3">Deduction Month</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {advances.map((adv: EmployeeAdvance) => (
                  <tr
                    key={adv.id}
                    className="border-b border-border/40 last:border-0 hover:bg-muted/30"
                  >
                    <td className="px-4 py-3 font-medium">
                      {adv.first_name} {adv.last_name}
                      {adv.employee_code && (
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({adv.employee_code})
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">{formatCurrency(adv.amount)}</td>
                    <td className="px-4 py-3 text-xs">{adv.advance_date}</td>
                    <td className="px-4 py-3 text-xs">
                      {formatMonth(adv.deduction_year, adv.deduction_month)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${ADVANCE_STATUS_COLORS[adv.status] ?? ''}`}
                      >
                        {adv.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {adv.status === 'pending' && (
                        <div className="flex items-center gap-1">
                          <button
                            title="Edit advance"
                            className="rounded p-1 text-muted-foreground hover:bg-muted"
                            onClick={() => setEditAdvance(adv)}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            title="Cancel advance"
                            className="rounded p-1 text-destructive hover:bg-destructive/10"
                            onClick={() => setCancelAdvanceId(adv.id)}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {advData && advData.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border/40 px-4 py-3 text-sm">
            <span className="text-muted-foreground">
              Page {advData.pagination.page} of {advData.pagination.totalPages} ·{' '}
              {advData.pagination.total} total
            </span>
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="outline"
                disabled={advPage <= 1}
                onClick={() => setAdvPage((p) => p - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={advPage >= advData.pagination.totalPages}
                onClick={() => setAdvPage((p) => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <AddLoanModal open={addLoanOpen} onClose={() => setAddLoanOpen(false)} />
      <AddAdvanceModal open={addAdvanceOpen} onClose={() => setAddAdvanceOpen(false)} />
      {editLoan && (
        <EditLoanModal loan={editLoan} open={!!editLoan} onClose={() => setEditLoan(null)} />
      )}
      {editAdvance && (
        <EditAdvanceModal advance={editAdvance} open={!!editAdvance} onClose={() => setEditAdvance(null)} />
      )}

      <AlertDialog open={!!closeLoanId} onOpenChange={(o) => { if (!o) setCloseLoanId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close this loan?</AlertDialogTitle>
            <AlertDialogDescription>
              The loan will be marked as closed and no further EMIs will be deducted. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { if (closeLoanId) updateLoanStatus.mutate({ id: closeLoanId, status: 'closed' }); setCloseLoanId(null); }}
            >
              Close Loan
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!cancelAdvanceId} onOpenChange={(o) => { if (!o) setCancelAdvanceId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this advance?</AlertDialogTitle>
            <AlertDialogDescription>
              The advance will be cancelled and will not be deducted from the employee's salary.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { if (cancelAdvanceId) cancelAdvance.mutate(cancelAdvanceId); setCancelAdvanceId(null); }}
            >
              Cancel Advance
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {scheduleLoan && (
        <LoanScheduleModal loan={scheduleLoan} onClose={() => setScheduleLoan(null)} />
      )}
      {payLoan && (
        <RecordLoanPaymentModal loan={payLoan} onClose={() => setPayLoan(null)} />
      )}
      {historyLoan && (
        <LoanHistoryModal loan={historyLoan} onClose={() => setHistoryLoan(null)} />
      )}
    </div>
  );
}

// ── Salary Overview Tab ─────────────────────────────────────────────────────────

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function SalaryOverviewTab() {
  const navigate = useNavigate();
  const { has } = usePermission();
  const canView = has(PERMISSIONS.SALARY_VIEW_ALL);

  const now = new Date();
  const [year, setYear]   = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [page, setPage]   = useState(1);
  const [search, setSearch] = useState('');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['payroll', 'salary-overview', { year, month, page, q: search || undefined }],
    queryFn: () =>
      payrollService.listSalaryOverview({
        year, month, page, limit: 20,
        q: search || undefined,
      }),
    enabled: canView,
  });

  const yearOptions = Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i);

  const [exporting, setExporting] = useState(false);

  async function handleExport() {
    setExporting(true);
    try {
      await payrollService.exportSalaryOverview({ year, month, q: search || undefined });
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setExporting(false);
    }
  }

  const columns: Column<SalaryOverviewRecord>[] = [
    {
      key: 'employee',
      header: 'Employee',
      cell: (r) => (
        <div>
          <span className="font-medium">
            {r.first_name} {r.last_name}
          </span>
          {r.employee_code && (
            <span className="ml-1.5 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
              {r.employee_code}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'department',
      header: 'Department',
      cell: (r) => (
        <span className="text-sm text-muted-foreground">{r.department_name ?? '—'}</span>
      ),
    },
    {
      key: 'designation',
      header: 'Designation',
      cell: (r) => (
        <span className="text-sm text-muted-foreground">{r.designation_name ?? '—'}</span>
      ),
    },
    {
      key: 'rate',
      header: 'Rate',
      className: NUM_COL,
      cell: (r) => (
        <span className="text-xs text-muted-foreground">
          ₹{parseFloat(r.per_hour_rate_snapshot).toLocaleString('en-IN')}/hr
        </span>
      ),
    },
    {
      key: 'working',
      header: 'Working',
      className: NUM_COL,
      cell: (r) => <HoursCell minutes={r.working_minutes} />,
    },
    {
      key: 'grace',
      header: 'Grace',
      className: NUM_COL,
      cell: (r) => <HoursCell minutes={r.grace_minutes} />,
    },
    {
      key: 'sunday',
      header: 'Sunday',
      className: NUM_COL,
      cell: (r) => <HoursCell minutes={r.sunday_effective_minutes} />,
    },
    {
      key: 'paid_leave',
      header: 'Paid Leave',
      className: NUM_COL,
      cell: (r) => <HoursCell minutes={r.paid_leave_minutes} />,
    },
    {
      key: 'penalty',
      header: 'Penalty',
      className: NUM_COL,
      cell: (r) =>
        r.penalty_minutes > 0 ? (
          <span className="text-rose-600">{formatHoursMinutes(r.penalty_minutes)}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'total_payable',
      header: 'Total Payable',
      className: NUM_COL,
      cell: (r) => (
        <span className="font-medium">{formatHoursMinutes(r.total_payable_minutes)}</span>
      ),
    },
    {
      key: 'deductions',
      header: 'Deductions',
      className: NUM_COL,
      cell: (r) => {
        const total =
          parseFloat(r.loan_deduction) +
          parseFloat(r.advance_deduction) +
          parseFloat(r.penalty_deduction);
        return total > 0 ? (
          <span className="text-rose-600">{formatCurrency(total)}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        );
      },
    },
    {
      key: 'net',
      header: 'Net Salary',
      className: NUM_COL,
      cell: (r) => (
        <span className="font-semibold text-teal-700">{formatCurrency(r.net_amount)}</span>
      ),
    },
    {
      key: 'computed',
      header: 'Computed',
      cell: (r) => (
        <span className="text-xs text-muted-foreground">
          {new Date(r.computed_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      cell: (r) => (
        <button
          title="View salary details"
          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          onClick={() => navigate(`/hrms/staff/${r.user_id}?tab=salary`)}
        >
          <ExternalLink className="h-4 w-4" />
        </button>
      ),
    },
  ];

  const monthLabel = `${MONTH_NAMES[month - 1]} ${year}`;

  return (
    <div className="rounded-lg border border-border/60 bg-card">
      {/* Header */}
      <div className="flex flex-col gap-3 border-b border-border/40 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold">Salary Overview</h2>
          <p className="text-xs text-muted-foreground">
            Computed salary records for all employees
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Year selector */}
          <Select
            value={String(year)}
            onValueChange={(val) => { setYear(Number(val)); setPage(1); }}
          >
            <SelectTrigger className="h-9 w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Month selector */}
          <Select
            value={String(month)}
            onValueChange={(val) => { setMonth(Number(val)); setPage(1); }}
          >
            <SelectTrigger className="h-9 w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTH_NAMES.map((name, idx) => (
                <SelectItem key={idx + 1} value={String(idx + 1)}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Search */}
          <SearchInput
            value={search}
            onChange={(v) => { setSearch(v); setPage(1); }}
            placeholder="Search employee…"
            containerClassName="flex-1 sm:flex-none sm:w-44"
          />

          {canView && (
            <Button
              variant="outline"
              size="sm"
              className="h-9"
              disabled={exporting || !data || data.data.length === 0}
              onClick={handleExport}
            >
              <Download className="mr-1.5 h-4 w-4" />
              {exporting ? 'Exporting…' : 'Export'}
            </Button>
          )}
        </div>
      </div>

      {/* Permission guard */}
      {!canView ? (
        <div className="px-4 py-8 text-center text-sm text-muted-foreground">
          You don't have permission to view salary records.
        </div>
      ) : (
        <DataTable<SalaryOverviewRecord>
          columns={columns}
          rows={data?.data ?? []}
          rowKey={(r) => r.id}
          loading={isLoading}
          error={isError}
          onRetry={() => refetch()}
          empty={
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              No salary records computed for {monthLabel}.
              <br />
              <span className="text-xs">
                Use the Compute button on each employee's salary tab to generate records.
              </span>
            </div>
          }
          pagination={
            data && data.pagination.totalPages > 1
              ? {
                  page: data.pagination.page,
                  totalPages: data.pagination.totalPages,
                  total: data.pagination.total,
                  onPageChange: setPage,
                }
              : undefined
          }
        />
      )}
    </div>
  );
}
